import {Button} from '@finpay-development/shared-components';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PermPhoneMsgIcon from '@mui/icons-material/PermPhoneMsg';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import {
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import moment from 'moment';
import React from 'react';

import {PfrEstimator} from '../../../admissions-advisor/models/estimator';
import {
  patientIocResult,
  patientSearchResult,
} from '../../../admissions-advisor/models/patient';
import {VobPostBody} from '../../../admissions-advisor/models/vob';
import {
  admissionsAdvisorUtils,
} from '../../../admissions-advisor/utils/admission-advisor-utils';
import {formatNumberToUSD} from '../../../shared/calculators';
import {
  ClientCrm,
  ClientStatusCardViewModel,
} from '../../../shared/model/client-status-card';
import classes from './estimates-search-modal.module.css';

interface EstimatesPatientRowProps {
  patient: patientSearchResult;
  patientIdToOpen: string;
  handleExpandPatientTable: (documentId: string) => void;
  handleModalClose: () => void;
  allClients: ClientStatusCardViewModel[] | undefined;
  allClientsMap: Map<number, any> | undefined;
  isLoadingFacilities: boolean;
  isLoadingFacilityLocPayers: boolean;
  onClickConfig?: {
    patient: (patientId: number) => void
    ioc: (patientId: number, encounterId: number) => void
    vob: (patientId: number) => void
    estimate: (patientId: number, encounterId?: number) => void
  };
}

const iocIcon = (
    <MedicalServicesIcon
        sx={{fontSize: '1.1em', display: 'inline'}}
        style={{color: 'blue'}}
    />
);
const estIcon = (
    <TaskAltIcon
        sx={{fontSize: '1.1em', display: 'inline'}}
        color="success"
    />
);
const vobIcon = (
    <AssignmentIcon
        sx={{fontSize: '1.1em', display: 'inline'}}
        style={{color: 'black'}}
    />
);

const iocIconLarge = (
    <MedicalServicesIcon
        sx={{fontSize: '1.5em', display: 'inline'}}
        style={{color: 'blue'}}
    />
);
const estIconLarge = (
    <TaskAltIcon
        sx={{fontSize: '1.5em', display: 'inline'}}
        color="success"
    />
);
const liveVobIconLarge = (
    <PermPhoneMsgIcon
        sx={{fontSize: '1.5em', display: 'inline'}}
        style={{color: 'black'}}
    />
);
const manualVobIconLarge = (
    <TouchAppIcon
        sx={{fontSize: '1.5em', display: 'inline'}}
        style={{color: 'black'}}
    />
);

const bottomBorderStyle = {borderBottom: 'none'};

const butStyleOverride = {
  border: '1px solid #557DBC',
  fontWeight: 'normal',
  fontSize: '1em',
  margin: '0',
  padding: '0',
};

const EstimatesPatientRow = (props: EstimatesPatientRowProps) => {
  const {
    patient,
    patientIdToOpen,
    handleExpandPatientTable,
    handleModalClose,
    allClients,
    allClientsMap,
    isLoadingFacilities,
    isLoadingFacilityLocPayers,
    onClickConfig,
  } = props;

  const dataId = patient._id;
  const sourceIndex = patient._index;
  const isExpanded = patientIdToOpen === dataId;

  const getLevelOfCareFromEstimate = (estimate: any) => {
    var list = '';
    for (
        let i = 0;
        i < estimate.estimateBody?.selectedLevelsOfCare?.length;
        i++
    ) {
      let loc = estimate.estimateBody.selectedLevelsOfCare[i];
      list += loc.facilityLevelOfCareCode;
      list += ', ';
    }
    if (list.length > 0) {
      list = list.substring(0, list.length - 2);
    } else {
      list = '';
    }
    return list;
  };

  const getLevelOfCareFromNewEstimate = (estimate: any) => {
    var list = '';
    for (
        let i = 0;
        i < estimate.estimateBody?.facilityLevelOfCare?.length;
        i++
    ) {
      let loc = estimate.estimateBody.facilityLevelOfCare[i];
      list += loc.facilityLevelOfCareCode;
      list += ', ';
    }
    if (list.length > 0) {
      list = list.substring(0, list.length - 2);
    } else {
      list = '';
    }
    return list;
  };

  const getPfrFromEstimate = (estimate: any) => {
    let pfr = admissionsAdvisorUtils.getSelectedPFR(estimate.estimateBody);
    if (!pfr) {
      pfr = 0;
    }
    return formatNumberToUSD(pfr);
  };

  const getPfrFromNewEstimate = (estimate: any) => {
    let pfr = estimate.estimateBody?.summary?.totalPFR;
    return formatNumberToUSD(pfr);
  };

  const getStatusFromIoc = (ioc: any) => {
    return ioc.workflow?.workflowStatus?.workflowStatusDesc
        ? ioc.workflow?.workflowStatus?.workflowStatusDesc
        : '';
  };

  const isIntegrationClient = (vob: any): boolean => {
    const clientItem = allClients?.find(
        (client: ClientStatusCardViewModel) =>
            client.clientId === vob.fpClientId,
    );
    const clientCrm: ClientCrm[] = clientItem?.clientCrm || [];
    return clientCrm.length > 0;
  };

  const patientDocumentDetails = () => {
    const vobLength =
        patient._source?.vobs?.length! > 0 ? patient._source?.vobs?.length : 0;
    let estimateLength = 0;
    if (vobLength! > 0) {
      patient?._source.vobs?.forEach((vob: VobPostBody) => {
        vob.estimates?.forEach((est: PfrEstimator) => {
          estimateLength++;
        });
      });
    }

    const iocLength =
        patient._source?.iocs?.length! > 0 ? patient._source?.iocs?.length : 0;

    return (
        <div style={{display: 'table', width: '100%'}}>
          <div style={{float: 'left', width: '30%', textAlign: 'left'}}>
            {vobLength! > 0 ? (
                <Typography
                    sx={{
                      fontSize: '1.0em',
                      display: 'inline',
                      marginRight: '0.5em',
                    }}
                >
                  {vobIcon}&nbsp;
                  {vobLength} VOB
                  {vobLength === 1 ? (
                      <span style={{color: 'transparent'}}>s</span>
                  ) : (
                      <span>s</span>
                  )}
                </Typography>
            ) : (
                <span>&nbsp;</span>
            )}
          </div>
          <div style={{float: 'left', width: '40%', textAlign: 'center'}}>
            {estimateLength! > 0 ? (
                <Typography sx={{fontSize: '1.0em', display: 'inline'}}>
                  {estIcon}&nbsp;
                  {estimateLength} Estimate
                  {estimateLength === 1 ? (
                      <span style={{color: 'transparent'}}>s</span>
                  ) : (
                      <span>s</span>
                  )}
                </Typography>
            ) : (
                <span>&nbsp;</span>
            )}
          </div>
          <div style={{float: 'left', width: '30%', textAlign: 'right'}}>
            {iocLength! > 0 ? (
                <Typography sx={{fontSize: '1.0em', display: 'inline'}}>
                  {iocIcon}&nbsp;
                  {iocLength} IOC
                  {iocLength === 1 ? (
                      <span style={{color: 'transparent'}}>s</span>
                  ) : (
                      <span>s</span>
                  )}
                </Typography>
            ) : (
                <span>&nbsp;</span>
            )}
          </div>
        </div>
    );
  };

  const iocExistsInEstimate = (iocId: number) => {
    const vob = patient?._source?.vobs?.find((x) =>
        x.estimates?.find(
            (y) => y.estimateBody?.finPay?.patientEncounterId === iocId,
        ),
    );
    return !!vob;
  };

  const iocExistsInNewEstimate = (iocId: number) => {
    const vob = patient?._source?.vobs?.find((x) =>
        x.estimates?.find(
            (y) => y.estimateBody?.patientEncounterId === iocId,
        ),
    );
    return !!vob;
  };

  const iocDetail = (
      ioc: patientIocResult,
      vobId: number | undefined,
      estimateId: number | undefined,
  ) => {
    return (
        <>
          <hr className={classes.hrSeperator}/>
          <Table
              size="small"
              width={'100%'}
              className={classes.lightSectionBackground}
          >
            <TableBody>
              <TableRow>
                <TableCell width="90%" style={{borderBottomWidth: '0px'}}>
                  {iocIconLarge}
                  &nbsp;IOC #{ioc.patientEncounterId}
                  &nbsp;&nbsp;&nbsp;{getStatusFromIoc(ioc)}
                </TableCell>
                <TableCell
                    width="10%"
                    rowSpan={2}
                    style={{borderBottomWidth: '0px'}}
                    className="p-1"
                >
                  {ioc.workflow.workflowName !== 'Converted' &&
                      (patient._source.patientId ||
                          patient._source.fpPatientId) && (
                              onClickConfig ? (
                                  <Button
                                      type="secondary"
                                      style={butStyleOverride}
                                      spinnerLeftPosition={1}
                                      loading={false}
                                      onClick={()=>{
                                        onClickConfig.ioc(patient._source.patientId ||
                                            patient._source.fpPatientId, ioc.patientEncounterId)
                                      }}
                                  >
                                    Select
                                  </Button>
                                  ): (
                                  <a
                                      href={estimateId ?
                                          `/patients/${patient._source.patientId ||
                                          patient._source.fpPatientId}/estimate/${estimateId}` :
                                          `/patients/${patient._source.patientId ||
                                          patient._source.fpPatientId}/estimate`}
                                      onClick={(e) => {
                                        if (e.button === 0 && !e.ctrlKey &&
                                            !e.metaKey) {
                                          e.preventDefault();
                                          handleModalClose();
                                          window.location.href = e.currentTarget.href;
                                        }
                                      }}
                                      style={{textDecoration: 'none'}}
                                  >
                                    <Button
                                        type="secondary"
                                        style={butStyleOverride}
                                        spinnerLeftPosition={1}
                                        loading={false}
                                    >
                                      Select
                                    </Button>
                                  </a>
                                  )
                      )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                    width="90%"
                    style={{borderBottomWidth: '0px', paddingLeft: '50px'}}
                >
                <span>
                  <span className={classes.propertyName}>
                    Creation Date:&nbsp;
                  </span>
                  {admissionsAdvisorUtils.formatDateTime(
                      new Date(ioc.instanceCreateDt),
                  )}
                </span>
                  {ioc.admissionDt && (
                      <span>
                    <br/>
                    <span className={classes.propertyName}>
                      Admission Date:&nbsp;
                    </span>
                        {ioc.admissionDt
                            ? admissionsAdvisorUtils.formatDateTime(
                                new Date(ioc.admissionDt),
                            )
                            : 'unknown'}
                  </span>
                  )}
                  {ioc.clientsPatientAccountId && (
                      <span>
                    <br/>
                    <span className={classes.propertyName}>
                      CRM Account ID:&nbsp;
                    </span>
                        {ioc.clientsPatientAccountId}
                  </span>
                  )}
                  {ioc.clientsPatientIOCId && (
                      <span>
                    <br/>
                    <span className={classes.propertyName}>
                      CRM Opportunity/Lead ID:&nbsp;
                    </span>
                        {ioc.clientsPatientIOCId}
                  </span>
                  )}
                  {(ioc.pfrAmt || ioc.levelOfCare) && (
                      <span>
                    <br/>
                        {ioc.pfrAmt && (
                            <>
                        <span className={classes.propertyName}>
                          PFR Amount:&nbsp;
                        </span>
                              {ioc.pfrAmt ?
                                  formatNumberToUSD(ioc.pfrAmt) :
                                  'unknown'}
                              &nbsp;&nbsp;&nbsp;
                            </>
                        )}
                        {ioc.levelOfCare && (
                            <>
                        <span className={classes.propertyName}>
                          Level Of Care:&nbsp;
                        </span>
                              {ioc.levelOfCare}
                            </>
                        )}
                  </span>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </>
    );
  };

  const findEstimateIoc = (
      iocId: number,
      vobId: number | undefined,
      estimateId: number | undefined,
  ) => {
    const ioc = patient?._source?.iocs?.find(
        (x) => x.patientEncounterId === iocId,
    );
    if (ioc && ioc.pfrEstimateId === estimateId) {
      return (
          <TableRow>
            <TableCell
                colSpan={2}
                style={{
                  paddingLeft: '50px',
                  borderBottomWidth: '0px',
                  paddingRight: '0px',
                }}
            >
              {iocDetail(ioc, vobId, estimateId)}
            </TableCell>
          </TableRow>
      );
    } else {
      return <></>;
    }
  };

  const showIocIfNotPreviouslyShown = (ioc: patientIocResult) => {
    const hasBeenShown = iocExistsInEstimate(ioc.patientEncounterId);
    const hasBeenShownInNewEstimate = iocExistsInNewEstimate(
        ioc.patientEncounterId);
    return (hasBeenShown || hasBeenShownInNewEstimate) ? (
        <></>
    ) : (
        iocDetail(ioc, undefined, undefined)
    );
  };

  return (
      <>
        <TableRow key={dataId} sx={bottomBorderStyle}>
          <TableCell
              sx={bottomBorderStyle}
              align="right"
              width="10%"
          >
            <IconButton
                sx={{border: '1px solid #557DBC', borderRadius: 1}}
                size="small"
                onClick={() => handleExpandPatientTable(dataId)}
                style={{fontSize: '1.5rem'}}
                test-id={`${dataId}-patient-open`}
            >
              {isExpanded ? (
                  <>
                    Close <KeyboardArrowUpIcon/>
                  </>
              ) : (
                  <>
                    Open
                    <KeyboardArrowDownIcon/>
                  </>
              )}
            </IconButton>
          </TableCell>
          <TableCell sx={bottomBorderStyle} width="35%">
            <strong>
              {patient._source?.firstName}{' '}
              {patient._source?.lastName}
            </strong>
            {!isExpanded ? (
                <>
                  <br/> DOB:{' '}
                  {moment(patient._source?.birthDate).format('l')}
                </>
            ) : (
                <>
                  <br/>
                  <br/>
                </>
            )}
          </TableCell>
          <TableCell sx={bottomBorderStyle} width="55%">
            <div style={{textAlign: 'left'}}>
              {patient._source?.email ? (
                  patient._source?.email
              ) : (
                  <i>email unavailable</i>
              )}
              {patient._source?.clientId && (
                  <>
                    <br/>
                    <span>
                  {allClientsMap?.get(patient._source?.clientId)?.clientName ||
                      'Unknown Client'}
                </span>
                  </>
              )}
            </div>
            {!isExpanded ? (
                patientDocumentDetails()
            ) : (
                <br/>
            )}
          </TableCell>
        </TableRow>
        <TableRow
            style={{
              paddingTop: '0px',
              paddingBottom: '0px',
              marginTop: '0px',
              marginBottom: '0px',
            }}
            key={`${dataId}-r2`}
        >
          <TableCell
              colSpan={3}
              style={{
                marginLeft: '30px',
                paddingTop: '0px',
                marginTop: '0px',
                backgroundColor: 'transparent',
                paddingBottom: '0px',
                marginBottom: '0px',
              }}
          >
            <Collapse
                in={isExpanded}
                timeout="auto"
                unmountOnExit
                className={'m-3 p-1 ' + classes.darkSectionBackground}
                style={{marginLeft: '80px', marginTop: '0px'}}
            >
              <>
                <Typography variant="body2">Patient Information</Typography>
                <Table
                    size="small"
                    width={'100%'}
                    className={classes.noSectionBackground}
                >
                  <TableBody>
                    <TableRow>
                      <TableCell width="90%" style={{borderBottomWidth: '0px'}}>
                        <Typography variant="body2">
                          <>
                            <span
                                className={classes.propertyName}>DOB:&nbsp;</span>
                            {moment(patient._source?.birthDate).format('l')}
                          </>
                        </Typography>
                      </TableCell>
                      <TableCell
                          width="10%"
                          align="right"
                          className="p-1"
                          style={{borderBottomWidth: '0px'}}
                      >
                        {(patient._source.patientId ||
                            patient._source.fpPatientId) && (
                            onClickConfig ? (
                                <Button
                                    type="secondary"
                                    style={butStyleOverride}
                                    spinnerLeftPosition={1}
                                    loading={isLoadingFacilityLocPayers ||
                                        isLoadingFacilities}
                                    test-id={`${patient._source.patientId ||
                                    patient._source.fpPatientId}-patient-select`}
                                    onClick={() => {
                                      onClickConfig.patient(patient._source.patientId ||
                                          patient._source.fpPatientId)
                                    }}
                                >
                                  Select
                                </Button>
                            ) : (
                                <a
                                    href={`/patients/${patient._source.patientId ||
                                    patient._source.fpPatientId}`}
                                    onClick={(e) => {
                                      if (e.button === 0 && !e.ctrlKey &&
                                          !e.metaKey) {
                                        e.preventDefault();
                                        handleModalClose();
                                        window.location.href = e.currentTarget.href;
                                      }
                                    }}
                                    style={{textDecoration: 'none'}}
                                >
                                  <Button
                                      type="secondary"
                                      style={butStyleOverride}
                                      spinnerLeftPosition={1}
                                      loading={isLoadingFacilityLocPayers ||
                                          isLoadingFacilities}
                                      test-id={`${patient._source.patientId ||
                                      patient._source.fpPatientId}-patient-select`}
                                  >
                                    Select
                                  </Button>
                                </a>
                            )

                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                {patient._source?.vobs?.map((vob: VobPostBody) => (
                    <React.Fragment key={vob.vobId}>
                      <hr className={classes.hrSeperator}/>
                      <Table
                          size="small"
                          width={'100%'}
                          className={classes.lightSectionBackground}
                      >
                        <TableBody>
                          <TableRow>
                            <TableCell
                                width="90%"
                                style={{borderBottomWidth: '0px'}}
                            >
                              {vob.vobBody?.liveVOB ? (
                                  <>
                                    {liveVobIconLarge} Live VOB #{vob.vobId}
                                    {vob.fpClientFacilityId && (
                                        <span style={{
                                          marginLeft: '10px',
                                          fontSize: '0.9em',
                                          color: '#666',
                                        }}>
                                  - {allClientsMap?.get(vob.fpClientId!)?.
                                                clientFacilities?.
                                                find((f: any) => f.facilityId ===
                                                    vob.fpClientFacilityId)?.facilityName ||
                                            'Unknown Facility'}
                                </span>
                                    )}
                                  </>
                              ) : (
                                  <>
                                    {manualVobIconLarge} Manual VOB #{vob.vobId}
                                    {vob.fpClientFacilityId && (
                                        <span style={{
                                          marginLeft: '10px',
                                          fontSize: '0.9em',
                                          color: '#666',
                                        }}>
                                  - {allClientsMap?.get(vob.fpClientId!)?.
                                                clientFacilities?.
                                                find((f: any) => f.facilityId ===
                                                    vob.fpClientFacilityId)?.facilityName ||
                                            'Unknown Facility'}
                                </span>
                                    )}
                                  </>
                              )}
                            </TableCell>
                            <TableCell
                                width="10%"
                                rowSpan={2}
                                style={{borderBottomWidth: '0px'}}
                                className="p-1"
                            >
                              {(patient._source.patientId ||
                                  patient._source.fpPatientId) && (
                                  onClickConfig ? (
                                      <Button
                                          type="secondary"
                                          style={butStyleOverride}
                                          spinnerLeftPosition={1}
                                          loading={isLoadingFacilityLocPayers ||
                                              isLoadingFacilities}
                                          test-id={`${vob.vobId}-vob-select`}
                                          onClick={()=>{
                                            onClickConfig.vob(patient._source.patientId ||
                                                patient._source.fpPatientId)
                                          }}
                                      >
                                        Select
                                      </Button>
                                      ): (
                                      <a
                                          href={`/patients/${patient._source.patientId ||
                                          patient._source.fpPatientId}/vob/${vob.vobId}`}
                                          onClick={(e) => {
                                            if (e.button === 0 && !e.ctrlKey &&
                                                !e.metaKey) {
                                              e.preventDefault();
                                              handleModalClose();
                                              window.location.href = e.currentTarget.href;
                                            }
                                          }}
                                          style={{textDecoration: 'none'}}
                                      >
                                        <Button
                                            type="secondary"
                                            style={butStyleOverride}
                                            spinnerLeftPosition={1}
                                            loading={isLoadingFacilityLocPayers ||
                                                isLoadingFacilities}
                                            test-id={`${vob.vobId}-vob-select`}
                                        >
                                          Select
                                        </Button>
                                      </a>
                                      )
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                                width="100%"
                                style={{
                                  borderBottomWidth: '0px',
                                  paddingLeft: '50px',
                                }}
                            >
                              {vob.createDt && (
                                  <span>
                              <span className={classes.propertyName}>
                                Date Pulled:&nbsp;
                              </span>
                                    {admissionsAdvisorUtils.formatDateTime(
                                        new Date(vob.createDt),
                                    )}
                            </span>
                              )}
                              {(vob.vobBody?.payer?.payorName ||
                                  vob.vobBody?.plan?.planName) && (
                                  <span>
                              <br/>
                              <span className={classes.propertyName}>
                                Payer/Plan:&nbsp;
                              </span>
                                    {vob.vobBody?.payer?.payorName
                                        ? vob.vobBody?.payer?.payorName
                                        : 'unknown'}
                                    &nbsp;/&nbsp;
                                    {vob.vobBody?.plan?.planName
                                        ? vob.vobBody?.plan?.planName
                                        : 'unknown'}
                            </span>
                              )}
                              {(vob.vobBody?.policyBeginDate ||
                                  vob.vobBody?.policyEndDate) && (
                                  <span>
                              <br/>
                              <span className={classes.propertyName}>
                                Policy Begin/End:&nbsp;
                              </span>
                                    {vob.vobBody.policyBeginDate
                                        ?
                                        moment(vob.vobBody.policyBeginDate).
                                            format('l')
                                        :
                                        'unknown'}
                                    &nbsp;-&nbsp;
                                    {vob.vobBody.policyEndDate
                                        ?
                                        moment(vob.vobBody.policyEndDate).
                                            format('l')
                                        :
                                        'unknown'}
                            </span>
                              )}
                            </TableCell>
                          </TableRow>

                          {vob.estimates?.map((est: PfrEstimator) =>
                          {
                            return (
                                <React.Fragment key={est.pfrEstimateId}>
                                  <TableRow>
                                    <TableCell
                                        colSpan={2}
                                        width="100%"
                                        style={{
                                          borderBottomWidth: '0px',
                                          paddingLeft: '50px',
                                          paddingRight: '0px',
                                        }}
                                    >
                                      <hr className={classes.hrSeperator}/>
                                      <Table size="small">
                                        <TableBody>
                                          <TableRow>
                                            <TableCell
                                                width="90%"
                                                style={{borderBottomWidth: '0px'}}
                                            >
                                              {estIconLarge} &nbsp;Estimate #
                                              {est.pfrEstimateId}
                                            </TableCell>
                                            <TableCell
                                                width="10%"
                                                rowSpan={2}
                                                style={{borderBottomWidth: '0px'}}
                                                className="p-1"
                                            >
                                              {(patient._source.patientId ||
                                                  patient._source.fpPatientId) &&
                                              onClickConfig ? (
                                                  <Button
                                                      type="secondary"
                                                      style={butStyleOverride}
                                                      spinnerLeftPosition={1}
                                                      loading={isLoadingFacilityLocPayers ||
                                                          isLoadingFacilities}
                                                      test-id={`${est.pfrEstimateId}-estimate-select`}
                                                      onClick={()=>{
                                                        onClickConfig.estimate(patient._source.patientId ||
                                                            patient._source.fpPatientId, est.estimateBody.patientEncounterId)
                                                      }}
                                                  >
                                                    Select
                                                  </Button>
                                              ): (
                                                  <a
                                                      href={`/patients/${patient._source.patientId ||
                                                      patient._source.fpPatientId}/estimate/${est.pfrEstimateId}`}
                                                      onClick={(e) => {
                                                        if (e.button === 0 &&
                                                            !e.ctrlKey &&
                                                            !e.metaKey) {
                                                          e.preventDefault();
                                                          handleModalClose();
                                                          window.location.href = e.currentTarget.href;
                                                        }
                                                      }}
                                                      style={{textDecoration: 'none'}}
                                                  >
                                                    <Button
                                                        type="secondary"
                                                        style={butStyleOverride}
                                                        spinnerLeftPosition={1}
                                                        loading={isLoadingFacilityLocPayers ||
                                                            isLoadingFacilities}
                                                        test-id={`${est.pfrEstimateId}-estimate-select`}
                                                    >
                                                      Select
                                                    </Button>
                                                  </a>
                                              )
                                              }
                                            </TableCell>
                                          </TableRow>
                                          <TableRow>
                                            <TableCell
                                                style={{
                                                  paddingLeft: '50px',
                                                  borderBottomWidth: '0px',
                                                }}
                                            >
                                      <span>
                                        <span className={classes.propertyName}>
                                          Creation Date:&nbsp;
                                        </span>
                                        {admissionsAdvisorUtils.formatDateTime(
                                            new Date(est.createDt),
                                        )}
                                      </span>
                                              {est.estimateBody.selectedPFRName &&
                                                  (
                                                      <span>
                                          <br/>
                                          <span
                                              className={classes.propertyName}>
                                            PFR:&nbsp;
                                          </span>
                                                        {getPfrFromEstimate(est)}
                                        </span>
                                                  )}
                                              {est.estimateBody?.summary && (
                                                  <span>
                                          <br/>
                                          <span
                                              className={classes.propertyName}>
                                            PFR:&nbsp;
                                          </span>
                                                    {getPfrFromNewEstimate(est)}
                                        </span>
                                              )}
                                              {est.estimateBody?.selectedLevelsOfCare &&
                                                  est.estimateBody?.selectedLevelsOfCare.length >
                                                  0 && (
                                                      <span>
                                            <br/>
                                            <span
                                                className={classes.propertyName}
                                            >
                                              LOCs:&nbsp;
                                            </span>
                                                        {getLevelOfCareFromEstimate(
                                                            est)}
                                          </span>
                                                  )}
                                              {est.estimateBody?.facilityLevelOfCare! &&
                                                  est.estimateBody?.facilityLevelOfCare.length >
                                                  0 && (
                                                      <span>
                                            <br/>
                                            <span
                                                className={classes.propertyName}
                                            >
                                              LOCs:&nbsp;
                                            </span>
                                                        {getLevelOfCareFromNewEstimate(
                                                            est)}
                                          </span>
                                                  )}
                                            </TableCell>
                                          </TableRow>
                                          {(est.estimateBody?.finPay?.patientEncounterId ||
                                                  est.estimateBody?.patientEncounterId) &&
                                              findEstimateIoc(
                                                  est.estimateBody?.finPay?.patientEncounterId ||
                                                  Number(
                                                      est.estimateBody?.patientEncounterId),
                                                  vob.vobId,
                                                  est.pfrEstimateId,
                                              )}
                                        </TableBody>
                                      </Table>
                                    </TableCell>
                                  </TableRow>
                                </React.Fragment>
                            )
                          }
                          )}
                        </TableBody>
                      </Table>
                    </React.Fragment>
                ))}
                {patient._source?.iocs?.map((ioc: patientIocResult) =>
                    showIocIfNotPreviouslyShown(ioc),
                )}
              </>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
  );
};

export default EstimatesPatientRow;
